import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Users, Plus, ChevronDown, Check, Trash2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FamilyProfileSwitcher = ({ currentUser, onProfileSwitch }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profiles, setProfiles] = useState({ main_account: null, children: [], active_child_id: null });
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProfiles = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/api/family/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(res.data);
    } catch (e) {
      console.error('Failed to load profiles');
    }
  };

  const addChildProfile = async () => {
    if (!newChildName.trim()) return;
    
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/api/family/profiles`, 
        { child_name: newChildName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewChildName('');
      setShowAddChild(false);
      loadProfiles();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add child');
    }
    setLoading(false);
  };

  const switchProfile = async (childId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.post(`${API}/api/family/switch`,
        { child_id: childId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsOpen(false);
      if (onProfileSwitch) {
        onProfileSwitch(res.data.active_child);
      }
      // Reload page to refresh all data
      window.location.reload();
    } catch (e) {
      setError('Failed to switch profile');
    }
    setLoading(false);
  };

  const deleteChildProfile = async (childId, childName) => {
    if (!window.confirm(`Are you sure you want to delete ${childName}'s profile? All their progress will be lost.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.delete(`${API}/api/family/profiles/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadProfiles();
    } catch (e) {
      setError('Failed to delete profile');
    }
  };

  const activeProfile = profiles.children.find(c => c.id === profiles.active_child_id) || profiles.main_account;
  const canAddMore = profiles.children.length < profiles.max_children;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
        data-testid="profile-switcher-btn"
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mint-green to-sky-blue flex items-center justify-center">
          <span className="text-white font-bold text-sm">
            {(activeProfile?.child_name || activeProfile?.username || 'U')[0].toUpperCase()}
          </span>
        </div>
        <span className="text-white font-medium hidden sm:block">
          {activeProfile?.child_name || activeProfile?.username || 'Profile'}
        </span>
        <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50">
          <div className="p-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Family Profiles</span>
            </div>
          </div>

          {error && (
            <div className="px-3 py-2 bg-red-50 text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="max-h-64 overflow-y-auto">
            {/* Main Account */}
            {profiles.main_account && !profiles.main_account.parent_id && (
              <button
                onClick={() => switchProfile(profiles.main_account.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                  !profiles.active_child_id ? 'bg-mint-green/10' : ''
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lavender to-coral-orange flex items-center justify-center">
                  <span className="text-white font-bold">
                    {(profiles.main_account.child_name || profiles.main_account.username || 'P')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900">
                    {profiles.main_account.child_name || profiles.main_account.username}
                  </div>
                  <div className="text-xs text-gray-500">Main Account</div>
                </div>
                {!profiles.active_child_id && (
                  <Check className="w-5 h-5 text-mint-green" />
                )}
              </button>
            )}

            {/* Child Profiles */}
            {profiles.children.map((child) => (
              <div
                key={child.id}
                className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                  profiles.active_child_id === child.id ? 'bg-mint-green/10' : ''
                }`}
              >
                <button
                  onClick={() => switchProfile(child.id)}
                  className="flex items-center gap-3 flex-1"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-blue to-mint-green flex items-center justify-center">
                    <span className="text-white font-bold">
                      {(child.child_name || child.username)[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-900">
                      {child.child_name || child.username}
                    </div>
                    <div className="text-xs text-gray-500">
                      Stage {child.island?.level_info?.stage || child.stage || 1} - Level {child.island?.level_info?.level || 1}
                    </div>
                  </div>
                  {profiles.active_child_id === child.id && (
                    <Check className="w-5 h-5 text-mint-green" />
                  )}
                </button>
                <button
                  onClick={() => deleteChildProfile(child.id, child.child_name)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Child Section */}
          <div className="border-t border-gray-200">
            {showAddChild ? (
              <div className="p-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Child's name"
                    value={newChildName}
                    onChange={(e) => setNewChildName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addChildProfile()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-mint-green"
                    autoFocus
                  />
                  <button
                    onClick={addChildProfile}
                    disabled={loading || !newChildName.trim()}
                    className="px-3 py-2 bg-mint-green text-white rounded-lg text-sm font-medium hover:bg-mint-green/90 disabled:opacity-50"
                  >
                    {loading ? '...' : 'Add'}
                  </button>
                  <button
                    onClick={() => { setShowAddChild(false); setNewChildName(''); }}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {profiles.children.length}/{profiles.max_children} children added
                </div>
              </div>
            ) : canAddMore ? (
              <button
                onClick={() => setShowAddChild(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-mint-green hover:bg-gray-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Add Child Profile</span>
              </button>
            ) : (
              <div className="px-4 py-3 text-center text-gray-500 text-sm">
                Maximum of {profiles.max_children} children reached
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyProfileSwitcher;
